import { useCallback, useEffect, useMemo, useState } from 'react'
import { Database, Loader2, Play, Trash2, Plus, Download, RotateCcw, AlertCircle, RefreshCw } from 'lucide-react'
import {
  backupApi,
  DOW_LABELS,
  dowMaskToFlags,
  flagsToDowMask,
  formatBuckets,
  formatBytes,
  normalizeStatus,
  SOURCE_KIND_LABELS,
  summarizeRetention,
  TIMEZONES,
} from '../../../../services/backupApi'
import { managedDevicesApi } from '../../../../services/managedDevicesApi'
import { formatDateTime } from '../../../../utils/format'

/**
 * Tab "Backup" no detalhe do ManagedDevice (FEA721).
 *
 *  A) Origens de backup do device — Linux: arquivo (com pre-script opcional);
 *     MikroTik: export + .backup coletados pelo routeros.service.
 *  B) Políticas do tenant — agenda (no fuso escolhido) + retenção por janela de tempo.
 *  C) Histórico — download (link temporário do S3) e restore (só Linux/mysql).
 */
export default function BackupSection({ device }) {
  const tenantId = device?.tenantId
  const deviceId = device?.id
  const isRouter = managedDevicesApi.isRouter(device)

  const [paths, setPaths] = useState([])
  const [policies, setPolicies] = useState([])
  const [executions, setExecutions] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [showPathForm, setShowPathForm] = useState(false)
  const [showPolicyForm, setShowPolicyForm] = useState(false)
  const [editingPolicy, setEditingPolicy] = useState(null)
  const [running, setRunning] = useState({})

  const loadAll = useCallback(async () => {
    if (!tenantId || !deviceId) return
    setLoading(true)
    setError(null)
    try {
      const [pol, pat, exe] = await Promise.all([
        backupApi.listPolicies(tenantId),
        backupApi.listPaths(deviceId),
        backupApi.listExecutions(deviceId, 200),
      ])
      setPolicies(pol || [])
      setPaths(pat || [])
      setExecutions(exe || [])
    } catch (e) {
      console.error('backup load:', e)
      setError(e.response?.data?.message || e.message)
    } finally {
      setLoading(false)
    }
  }, [tenantId, deviceId])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  const policiesById = useMemo(() => new Map(policies.map((p) => [p.id, p])), [policies])
  const pathsById = useMemo(() => new Map(paths.map((p) => [p.id, p])), [paths])

  const handleRun = async (pathId, policyId) => {
    const k = `${pathId}|${policyId}`
    setRunning((s) => ({ ...s, [k]: true }))
    try {
      const exec = await backupApi.runNow(pathId, policyId)
      if (normalizeStatus(exec?.status) === 'Failed') alert(`Backup falhou: ${exec.errorMessage || 'sem detalhes'}`)
      await loadAll()
    } catch (e) {
      alert(e.response?.data?.message || e.message)
    } finally {
      setRunning((s) => ({ ...s, [k]: false }))
    }
  }

  const handleDeletePath = async (id) => {
    if (!confirm('Remover esta origem de backup? Os backups já guardados continuam no histórico até a retenção.')) return
    await backupApi.deletePath(id)
    await loadAll()
  }

  const handleDeletePolicy = async (policy) => {
    const msg = policy.autoApplyToRouters
      ? 'Esta é uma política aplicada automaticamente aos MikroTiks. Se for a padrão, ela será recriada — para parar os backups, DESABILITE em vez de remover. Remover mesmo assim?'
      : 'Remover esta política?'
    if (!confirm(msg)) return
    await backupApi.deletePolicy(policy.id)
    await loadAll()
  }

  const handleDownload = async (executionId) => {
    try {
      const { url } = await backupApi.getDownloadUrl(executionId)
      window.open(url, '_blank', 'noopener')
    } catch (e) {
      alert(e.response?.data?.message || e.message)
    }
  }

  const handleRestore = async (executionId) => {
    const dbName = prompt('Restaurar para qual database (target)? Deixe vazio para inferir do path.')
    if (dbName === null) return
    if (!confirm(
      'CONFIRMA o restore? Isto irá SOBRESCREVER tabelas existentes no database alvo. ' +
      'Recomendado parar o serviço da app antes.'
    )) return
    try {
      const r = await backupApi.restore(executionId, dbName || undefined)
      alert(`${r.success ? 'OK' : 'FALHA'}: ${r.stage} — ${r.message || '(sem detalhes)'}`)
    } catch (e) {
      alert(e.response?.data?.message || e.message)
    }
  }

  if (!tenantId || !deviceId) return null

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1">
        <Database className="w-4 h-4 text-theme-primary" />
        <h3 className="text-sm font-semibold text-gray-900">Backup</h3>
        <button onClick={loadAll} className="ml-auto p-1 text-gray-500 hover:text-gray-800" title="Atualizar">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        {isRouter
          ? 'Backup diário do MikroTik (export da configuração + arquivo .backup) guardado no object storage (S3, privado). O export contém senhas — o download gera um link temporário.'
          : 'Backup de dados do device para o object storage (S3). Cada origem coleta um arquivo do device (opcionalmente após um pre-script).'}
      </p>

      {error && (
        <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 mb-3">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* ── Origens ─────────────────────────────────────── */}
      <div className="mt-2">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
            Origens ({paths.length})
          </h4>
          <button
            onClick={() => setShowPathForm(true)}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-theme-primary text-white rounded hover:opacity-90"
          >
            <Plus className="w-3 h-3" /> Adicionar origem
          </button>
        </div>
        {paths.length === 0 ? (
          <p className="text-xs text-gray-400 italic py-2">
            {isRouter
              ? 'Nenhuma origem ainda — com a política padrão de MikroTik ativa, ela é criada automaticamente em até 1 minuto.'
              : 'Nenhuma origem configurada.'}
          </p>
        ) : (
          <div className="space-y-1.5">
            {paths.map((p) => (
              <PathRow
                key={p.id}
                path={p}
                policies={policies}
                policiesById={policiesById}
                running={running}
                onRun={handleRun}
                onDelete={handleDeletePath}
                onUpdated={loadAll}
              />
            ))}
          </div>
        )}
        {showPathForm && (
          <PathForm
            isRouter={isRouter}
            policies={policies}
            onCancel={() => setShowPathForm(false)}
            onSubmit={async (form) => {
              await backupApi.createPath(deviceId, form)
              setShowPathForm(false)
              await loadAll()
            }}
          />
        )}
      </div>

      {/* ── Políticas ──────────────────────────────────── */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
            Políticas do tenant ({policies.length})
          </h4>
          <button
            onClick={() => { setEditingPolicy(null); setShowPolicyForm(true) }}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-theme-primary text-white rounded hover:opacity-90"
          >
            <Plus className="w-3 h-3" /> Nova política
          </button>
        </div>
        {policies.length === 0 ? (
          <p className="text-xs text-gray-400 italic py-2">Nenhuma política cadastrada para o tenant.</p>
        ) : (
          <div className="space-y-1.5">
            {policies.map((pol) => (
              <PolicyRow
                key={pol.id}
                policy={pol}
                onEdit={() => { setEditingPolicy(pol); setShowPolicyForm(true) }}
                onDelete={handleDeletePolicy}
              />
            ))}
          </div>
        )}
        {showPolicyForm && (
          <PolicyForm
            initial={editingPolicy}
            onCancel={() => { setShowPolicyForm(false); setEditingPolicy(null) }}
            onSubmit={async (form) => {
              if (editingPolicy) await backupApi.updatePolicy(editingPolicy.id, form)
              else await backupApi.createPolicy(tenantId, form)
              setShowPolicyForm(false)
              setEditingPolicy(null)
              await loadAll()
            }}
          />
        )}
      </div>

      {/* ── Histórico ─────────────────────────────────── */}
      <div className="mt-5">
        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
          Backups guardados e tentativas ({executions.length})
        </h4>
        {executions.length === 0 ? (
          <p className="text-xs text-gray-400 italic py-2">Nenhum backup executado ainda.</p>
        ) : (
          <div className="border border-gray-200 rounded overflow-x-auto">
            <table className="text-xs w-full">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-2 py-1 text-left font-medium">Quando</th>
                  <th className="px-2 py-1 text-left font-medium">Status</th>
                  <th className="px-2 py-1 text-left font-medium">Retido como</th>
                  <th className="px-2 py-1 text-right font-medium">Tamanho</th>
                  <th className="px-2 py-1 text-left font-medium">Política</th>
                  <th className="px-2 py-1 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {executions.map((e) => {
                  const status = normalizeStatus(e.status)
                  const kind = pathsById.get(e.backupPathId)?.sourceKind
                  return (
                    <tr key={e.id} className="border-t border-gray-100 align-top">
                      <td className="px-2 py-1 font-mono whitespace-nowrap">{formatDateTime(e.startedAt)}</td>
                      <td className="px-2 py-1">
                        <StatusBadge status={status} />
                        {e.errorMessage && (
                          <div className={`mt-0.5 max-w-[40ch] truncate ${status === 'Failed' ? 'text-red-600' : 'text-amber-700'}`} title={e.errorMessage}>
                            {e.errorMessage}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1">{status === 'Success' ? formatBuckets(e.buckets) : '—'}</td>
                      <td className="px-2 py-1 text-right whitespace-nowrap">{status === 'Success' ? formatBytes(e.sizeBytes) : '—'}</td>
                      <td className="px-2 py-1 text-gray-600">{policiesById.get(e.backupPolicyId)?.name || '—'}</td>
                      <td className="px-2 py-1 text-right space-x-1 whitespace-nowrap">
                        {status === 'Success' && (
                          <>
                            <button
                              title="Baixar (link temporário)"
                              onClick={() => handleDownload(e.id)}
                              className="p-1 text-gray-600 hover:text-theme-primary"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </button>
                            {kind !== 'RouterOsExport' && (
                              <button
                                title="Restaurar"
                                onClick={() => handleRestore(e.id)}
                                className="p-1 text-gray-600 hover:text-amber-600"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {isRouter && (
          <p className="mt-2 text-[11px] text-gray-400">
            Restore de MikroTik é manual: baixe o arquivo e aplique <span className="font-mono">export.rsc</span> (/import) ou{' '}
            <span className="font-mono">system.backup</span> (/system backup load, mesmo modelo).
          </p>
        )}
      </div>
    </section>
  )
}

// ── Subcomponentes ─────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  if (status === 'Running') return <span className="text-gray-600">Rodando…</span>
  if (status === 'Success') return <span className="text-green-700">OK</span>
  if (status === 'Failed') return <span className="text-red-600">Falhou</span>
  return <span>—</span>
}

function PathRow({ path, policies, policiesById, running, onRun, onDelete, onUpdated }) {
  const linkedPolicies = (path.policyIds || []).map((id) => policiesById.get(id)).filter(Boolean)
  const isRouterExport = path.sourceKind === 'RouterOsExport'
  return (
    <div className="p-2 bg-gray-50 border border-gray-200 rounded">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900">
            {path.name}
            {!path.enabled && <span className="ml-2 text-xs text-amber-700">(desabilitada)</span>}
          </div>
          <div className="text-xs text-gray-600 truncate">
            {isRouterExport ? SOURCE_KIND_LABELS.RouterOsExport : <span className="font-mono">{path.sourcePath}</span>}
          </div>
          {!isRouterExport && path.preScript && (
            <div className="text-xs text-gray-500 mt-0.5">
              <span className="font-mono">pre:</span> <span className="font-mono">{path.preScript}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onDelete(path.id)} className="p-1 text-gray-500 hover:text-red-600" title="Remover">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="mt-2 space-y-1">
        {linkedPolicies.length === 0 ? (
          <div className="text-xs text-gray-400 italic">Nenhuma política associada.</div>
        ) : (
          linkedPolicies.map((pol) => {
            const k = `${path.id}|${pol.id}`
            return (
              <div key={pol.id} className="flex items-center justify-between text-xs gap-2">
                <span className="text-gray-700">
                  <span className="font-medium">{pol.name}</span>
                  <span className="text-gray-400 ml-1">({pol.timeOfDay} · {summarizeRetention(pol)})</span>
                </span>
                <button
                  onClick={() => onRun(path.id, pol.id)}
                  disabled={running[k]}
                  className="inline-flex items-center gap-1 text-xs px-2 py-0.5 border border-gray-300 rounded hover:bg-white disabled:opacity-50"
                >
                  {running[k] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                  Executar agora
                </button>
              </div>
            )
          })
        )}
        <PathPolicyAttach
          policies={policies}
          linked={linkedPolicies.map((p) => p.id)}
          onAttach={async (policyId) => {
            await backupApi.attachPolicy(path.id, policyId)
            onUpdated()
          }}
          onDetach={async (policyId) => {
            await backupApi.detachPolicy(path.id, policyId)
            onUpdated()
          }}
        />
      </div>
    </div>
  )
}

function PathPolicyAttach({ policies, linked, onAttach, onDetach }) {
  const [mode, setMode] = useState(null) // 'attach' | 'detach' | null
  const available = policies.filter((p) => !linked.includes(p.id))
  const attached = policies.filter((p) => linked.includes(p.id))

  if (!mode) {
    return (
      <div className="flex gap-3 mt-1">
        {available.length > 0 && (
          <button onClick={() => setMode('attach')} className="text-xs text-theme-primary hover:underline">
            + Associar política
          </button>
        )}
        {attached.length > 0 && (
          <button onClick={() => setMode('detach')} className="text-xs text-gray-500 hover:underline">
            − Desassociar política
          </button>
        )}
      </div>
    )
  }

  const options = mode === 'attach' ? available : attached
  return (
    <div className="flex gap-1 mt-1">
      <select
        className="text-xs border border-gray-300 rounded px-1 py-0.5"
        defaultValue=""
        onChange={async (e) => {
          if (!e.target.value) return
          if (mode === 'attach') await onAttach(e.target.value)
          else await onDetach(e.target.value)
          setMode(null)
        }}
      >
        <option value="">{mode === 'attach' ? 'Associar…' : 'Desassociar…'}</option>
        {options.map((p) => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
      </select>
      <button onClick={() => setMode(null)} className="text-xs text-gray-500">cancelar</button>
    </div>
  )
}

function PolicyRow({ policy, onEdit, onDelete }) {
  const flags = dowMaskToFlags(policy.daysOfWeekMask)
  return (
    <div className="p-2 bg-gray-50 border border-gray-200 rounded flex items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-900">
          {policy.name}
          {policy.autoApplyToRouters && (
            <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-theme-primary/10 text-theme-primary">todos os MikroTiks</span>
          )}
          {!policy.enabled && <span className="ml-2 text-xs text-amber-700">(desabilitada)</span>}
        </div>
        <div className="text-xs text-gray-600">
          {DOW_LABELS.map((l, i) => (
            <span key={l} className={flags[i] ? 'text-theme-primary font-medium mr-1' : 'text-gray-300 mr-1'}>{l}</span>
          ))}
          <span className="ml-1 font-mono">{policy.timeOfDay}</span>
          <span className="ml-1 text-gray-400">({policy.timeZoneId})</span>
          <span className="ml-2 text-gray-500">{summarizeRetention(policy)}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={onEdit} className="text-xs text-gray-600 hover:text-theme-primary">Editar</button>
        <button onClick={() => onDelete(policy)} className="p-1 text-gray-500 hover:text-red-600" title="Remover">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  )
}

function PathForm({ isRouter, policies, onSubmit, onCancel }) {
  const [sourceKind, setSourceKind] = useState(isRouter ? 'RouterOsExport' : 'File')
  const [name, setName] = useState(isRouter ? 'MikroTik (export + backup)' : '')
  const [sourcePath, setSourcePath] = useState('')
  const [preScript, setPreScript] = useState('')
  const [policyIds, setPolicyIds] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const isFile = sourceKind === 'File'

  const submit = async (e) => {
    e.preventDefault()
    if (!name.trim() || (isFile && !sourcePath.trim())) return
    setSubmitting(true)
    try {
      await onSubmit({
        name: name.trim(),
        sourceKind,
        sourcePath: isFile ? sourcePath.trim() : '',
        preScript: isFile ? preScript.trim() || null : null,
        enabled: true,
        policyIds,
      })
    } catch (err) {
      alert(err.response?.data?.message || err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit} className="mt-2 p-3 bg-white border border-gray-300 rounded space-y-2 text-xs">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <label className="block">
          Tipo
          <select value={sourceKind} onChange={(e) => setSourceKind(e.target.value)} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1">
            <option value="RouterOsExport">{SOURCE_KIND_LABELS.RouterOsExport}</option>
            <option value="File">{SOURCE_KIND_LABELS.File}</option>
          </select>
        </label>
        <label className="block">
          Nome
          <input value={name} onChange={(e) => setName(e.target.value)} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1" required />
        </label>
      </div>
      {isFile && (
        <>
          <label className="block">
            Diretório no device (coleta o arquivo mais recente)
            <input value={sourcePath} onChange={(e) => setSourcePath(e.target.value)} placeholder="/mnt/data/apps/aeromais-server/backups" className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 font-mono" required />
          </label>
          <label className="block">
            Pre-script (opcional)
            <input value={preScript} onChange={(e) => setPreScript(e.target.value)} placeholder="/opt/aeromais/scripts/backup_database.sh" className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 font-mono" />
          </label>
        </>
      )}
      <div>
        Políticas a associar:
        <div className="mt-1 space-y-1">
          {policies.length === 0 ? (
            <span className="text-gray-400 italic">Nenhuma política disponível — crie uma primeiro.</span>
          ) : (
            policies.map((pol) => (
              <label key={pol.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={policyIds.includes(pol.id)}
                  onChange={(e) => setPolicyIds((s) => (e.target.checked ? [...s, pol.id] : s.filter((id) => id !== pol.id)))}
                />
                <span>{pol.name}</span>
              </label>
            ))
          )}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1 border border-gray-300 rounded">Cancelar</button>
        <button type="submit" disabled={submitting} className="px-3 py-1 bg-theme-primary text-white rounded disabled:opacity-50">
          {submitting ? 'Salvando…' : 'Criar'}
        </button>
      </div>
    </form>
  )
}

function PolicyForm({ initial, onSubmit, onCancel }) {
  const [name, setName] = useState(initial?.name ?? '')
  const [dowFlags, setDowFlags] = useState(initial ? dowMaskToFlags(initial.daysOfWeekMask) : [true, true, true, true, true, true, true])
  const [timeOfDay, setTimeOfDay] = useState(initial?.timeOfDay ?? '03:00')
  const [timeZoneId, setTimeZoneId] = useState(initial?.timeZoneId ?? 'America/Sao_Paulo')
  const [retainDaily, setRetainDaily] = useState(initial?.retainDaily ?? 30)
  const [retainWeekly, setRetainWeekly] = useState(initial?.retainWeekly ?? 0)
  const [retainMonthly, setRetainMonthly] = useState(initial?.retainMonthly ?? 12)
  const [retainYearly, setRetainYearly] = useState(initial?.retainYearly ?? 0)
  const [autoApplyToRouters, setAutoApplyToRouters] = useState(initial?.autoApplyToRouters ?? false)
  const [enabled, setEnabled] = useState(initial?.enabled ?? true)
  const [submitting, setSubmitting] = useState(false)

  const zones = TIMEZONES.includes(timeZoneId) ? TIMEZONES : [timeZoneId, ...TIMEZONES]

  const submit = async (e) => {
    e.preventDefault()
    if (!name.trim()) return
    setSubmitting(true)
    try {
      await onSubmit({
        name: name.trim(),
        daysOfWeekMask: flagsToDowMask(dowFlags),
        timeOfDay,
        timeZoneId,
        retainDaily: Math.max(0, Number(retainDaily) || 0),
        retainWeekly: Math.max(0, Number(retainWeekly) || 0),
        retainMonthly: Math.max(0, Number(retainMonthly) || 0),
        retainYearly: Math.max(0, Number(retainYearly) || 0),
        autoApplyToRouters,
        enabled,
      })
    } catch (err) {
      alert(err.response?.data?.message || err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const numberField = (label, hint, value, setter) => (
    <label className="block">
      {label}
      <input type="number" min="0" value={value} onChange={(e) => setter(e.target.value)} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1" />
      <span className="text-[11px] text-gray-400">{hint}</span>
    </label>
  )

  return (
    <form onSubmit={submit} className="mt-2 p-3 bg-white border border-gray-300 rounded space-y-3 text-xs">
      <label className="block">
        Nome
        <input value={name} onChange={(e) => setName(e.target.value)} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1" required />
      </label>
      <div>
        Dias da semana
        <div className="flex gap-1 mt-1">
          {DOW_LABELS.map((l, i) => (
            <button
              key={l}
              type="button"
              onClick={() => setDowFlags((flags) => flags.map((v, idx) => (idx === i ? !v : v)))}
              className={`px-2 py-1 border rounded ${dowFlags[i] ? 'bg-theme-primary text-white border-theme-primary' : 'bg-white border-gray-300 text-gray-600'}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          Horário (HH:MM)
          <input value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)} pattern="[0-9]{2}:[0-9]{2}" className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 font-mono" required />
        </label>
        <label className="block">
          Fuso horário
          <select value={timeZoneId} onChange={(e) => setTimeZoneId(e.target.value)} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1">
            {zones.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        </label>
      </div>
      <div>
        <div className="font-medium text-gray-700 mb-1">Retenção</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {numberField('Diários (dias)', 'todos os backups dos últimos N dias', retainDaily, setRetainDaily)}
          {numberField('Semanais (semanas)', '1 por semana; 0 = não guardar', retainWeekly, setRetainWeekly)}
          {numberField('Mensais (meses)', '1 por mês; 0 = não guardar', retainMonthly, setRetainMonthly)}
          {numberField('Anuais (anos)', '1 por ano; 0 = ilimitado', retainYearly, setRetainYearly)}
        </div>
      </div>
      <div className="space-y-1">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={autoApplyToRouters} onChange={(e) => setAutoApplyToRouters(e.target.checked)} />
          Aplicar automaticamente a todos os MikroTiks do tenant (inclusive os novos)
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Habilitada
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1 border border-gray-300 rounded">Cancelar</button>
        <button type="submit" disabled={submitting} className="px-3 py-1 bg-theme-primary text-white rounded disabled:opacity-50">
          {submitting ? 'Salvando…' : initial ? 'Salvar' : 'Criar'}
        </button>
      </div>
    </form>
  )
}
